import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { Backdrop, Box, Divider, Grid, useTheme } from '@mui/material';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useOrderDetails } from 'components/context/order_details';
import { useUserJourneys } from 'components/context/user_journeys';
import PageLoader from 'components/common/page_loader';
import AddressSummary from 'components/common/summary/address_summary';
import PaymentSummary from 'components/common/summary/payment_summary';
import ContactSummary from 'components/common/summary/contact_summary';
import ShippingMethodSummary from 'components/common/summary/shipping_method_summary';
import Disclaimer from 'components/pages/checkout/review/disclaimer';
import {
  buildCheckoutPath,
  onCheckoutPageClick,
  onCheckoutPageView,
  onCheckoutPageViewV2,
  pushHistoryWithSearchParams,
} from 'components/pages/checkout/utilities';
import { localizedShopPath } from 'utilities/locales';
import { trackCdp } from 'utilities/tracking';
import SubmitWithRecaptcha from 'components/pages/checkout/review/submit_with_recaptcha';
import CDP_EVENT_NAMES from 'constants/cdp_events';
import { PAYMENT_RESPONSE_ERROR_STATUS_MAPPINGS } from 'constants/mappings';
import { isDashboardMode } from 'helpers/search_parameter_helpers';
import DashboardModeFooter from 'components/common/dashboard_mode_footer';
import { shopFooterDisclaimers } from 'utilities/disclaimers';
import { COMPONENT_NAMES } from 'constants/tracking';
import {
  FrictionSignal,
  FrustrationSignal,
  UserJourneyName,
} from 'constants/user_journeys';

const Review = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const theme = useTheme();

  const { trackingData } = useOrderDetails();
  const {
    checkoutClient,
    orderRefresh,
    setToast,
    payWithSavedCard,
    makeDefaultCard,
  } = useCheckoutFlow();
  const { userJourneyTracker } = useUserJourneys();

  const [isWarranty, setIsWarranty] = useState(false);
  const [noShip, setNoShip] = useState(false);
  const [orderData, setOrderData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [recaptchaResponse, setRecaptchaResponse] = useState(false);
  const [disclosureDocumentToken, setDisclosureDocumentToken] = useState(null);
  const [disclosureAcknowledged, setDisclosureAcknowledged] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    orderRefresh().then((data) => {
      setOrderData(data);
      setIsWarranty(data.is_warranty);
      setNoShip(data.no_ship);
    });
    onCheckoutPageView('Review Order', trackingData);
    onCheckoutPageViewV2({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT.REVIEW,
      additionalParameters: trackingData,
    });
  }, []);

  useEffect(() => {
    setProcessing(true);
    let documentToken;
    let successfulRequest = true;

    checkoutClient
      .createFinancingDisclosures()
      .then((resp) => {
        documentToken = resp.disclosure_document_token;
      })
      .catch(() => {
        successfulRequest = false;

        userJourneyTracker.addFrustrationSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frustrationSignal:
            FrustrationSignal.ReviewStepFinancingDisclosuresCreateError,
        });
      })
      .finally(() => {
        setProcessing(false);

        if (!successfulRequest || documentToken === undefined) {
          setToast({
            open: true,
            message: t('checkout.review.error.general'),
            variant: 'critical',
          });
          pushHistoryWithSearchParams(history, '/information');
        } else if (documentToken) {
          setDisclosureDocumentToken(documentToken);
        }
      });
  }, []);

  const resolve = () => {
    onCheckoutPageClick({
      eventDesc: 'Review and submit your order.',
      componentName: COMPONENT_NAMES.APPS.CHECKOUT.REVIEW,
      actionItem: 'complete_your_order',
      trackingData,
    });
    pushHistoryWithSearchParams(history, '/complete');
  };

  const reject = (json) => {
    json
      .then((data) => {
        if (
          !Object.values(PAYMENT_RESPONSE_ERROR_STATUS_MAPPINGS).includes(
            data.message,
          )
        ) {
          throw new Error(data.message);
        } else if (
          data.message ===
          PAYMENT_RESPONSE_ERROR_STATUS_MAPPINGS.ISSUER_INSTALLMENT_ERROR
        ) {
          // track issuer installment errors
          trackCdp({
            name: CDP_EVENT_NAMES.CHECKOUT.REVIEW
              .REACH_PAYMENT_ISSUER_INSTALLMENTS_ERROR,
            params: {
              feature: 'shop',
              step: 'Review Order',
              error:
                PAYMENT_RESPONSE_ERROR_STATUS_MAPPINGS.ISSUER_INSTALLMENT_ERROR,
              ...trackingData,
            },
          });
        }
        onCheckoutPageView('Review Order - Payment Error', trackingData);
        trackCdp({
          name: CDP_EVENT_NAMES.CHECKOUT.REVIEW.COMPLETING_ORDER_ERROR_PAYMENT,
          params: {
            feature: 'shop',
            step: 'Review Order',
            error: data.message,
            ...trackingData,
          },
        });
        setToast({
          open: true,
          message: t('checkout.review.error.payment'),
          variant: 'critical',
        });
        userJourneyTracker.addFrictionSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frictionSignal: FrictionSignal.ReviewStepOrderCompletionPaymentError,
        });

        pushHistoryWithSearchParams(history, '/payment');
      })
      .catch((error) => {
        setToast({
          open: true,
          message: t('checkout.review.error.general'),
          variant: 'critical',
        });
        onCheckoutPageView('Review Order - General Error', trackingData);
        trackCdp({
          name: CDP_EVENT_NAMES.CHECKOUT.REVIEW.COMPLETING_ORDER_ERROR_GENERAL,
          params: {
            feature: 'shop',
            step: 'Review Order',
            error: error.message,
            ...trackingData,
          },
        });
        userJourneyTracker.addFrustrationSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frustrationSignal: FrustrationSignal.ReviewStepOrderCompletionError,
        });
        pushHistoryWithSearchParams(history, '/information');
      });
  };

  const submit = () => {
    setSubmitting(true);
    checkoutClient
      .orderComplete(
        recaptchaResponse,
        disclosureAcknowledged,
        payWithSavedCard,
        makeDefaultCard,
      )
      .then(resolve, reject);
  };

  if (Object.entries(orderData).length === 0) {
    return <PageLoader />;
  }

  if (orderData.completed) {
    window.location = `${localizedShopPath()}/cart`;
    return <PageLoader />;
  }

  const infoSection = () => (
    <>
      <Divider sx={{ mb: 3 }} />
      <Grid item container rowSpacing={2}>
        <Grid item xs={10}>
          <ContactSummary contact={orderData.contact_information} />
        </Grid>
        <Grid item xs={2} sx={{ textAlign: 'right' }}>
          <Link to={`${buildCheckoutPath(history, '/information')}`}>
            {t('checkout.label.edit')}
          </Link>
        </Grid>
      </Grid>
      <Divider sx={{ my: 3 }} />
      <Grid item container rowSpacing={2}>
        <Grid item xs={10} md={5}>
          <h3>{t('checkout.summary.address.shippingTitle')}</h3>
          <AddressSummary address={orderData.shipping_address} />
        </Grid>
        <Grid item xs={2} sx={{ textAlign: 'right', display: { md: 'none' } }}>
          <Link to={`${buildCheckoutPath(history, '/information')}`}>
            {t('checkout.label.edit')}
          </Link>
        </Grid>
        <Grid item xs={10} md={5}>
          <h3>{t('checkout.summary.address.billingTitle')}</h3>
          <AddressSummary address={orderData.billing_address} />
        </Grid>
        <Grid item xs={2} sx={{ textAlign: 'right' }}>
          <Link to={`${buildCheckoutPath(history, '/information')}`}>
            {t('checkout.label.edit')}
          </Link>
        </Grid>
      </Grid>
      <Divider sx={{ my: 3 }} />
    </>
  );

  const shippingSection = () =>
    isWarranty || noShip ? null : (
      <>
        <Grid item container>
          <Grid item xs={10}>
            <ShippingMethodSummary
              shippingEstimate={orderData.shipping_estimate}
            />
          </Grid>
          <Grid item xs={2} sx={{ textAlign: 'right' }}>
            <Link to={`${buildCheckoutPath(history, '/shipping_method')}`}>
              {t('checkout.label.edit')}
            </Link>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
      </>
    );

  const paymentSection = () =>
    Object.entries(orderData.payment_information).length > 0 && (
      <>
        <Grid item container>
          <Grid item xs={10}>
            <PaymentSummary payment={orderData.payment_information} />
          </Grid>
          <Grid item xs={2} sx={{ textAlign: 'right' }}>
            <Link to={`${buildCheckoutPath(history, '/payment')}`}>
              {t('checkout.label.edit')}
            </Link>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
      </>
    );

  return (
    <>
      <Backdrop open={submitting} sx={{ zIndex: 'modal', color: '#fff' }}>
        <PageLoader />
      </Backdrop>
      {isWarranty && (
        <Box component="h2" sx={{ mb: 2 }}>
          {t('checkout.review.titleAlt')}
        </Box>
      )}
      <Grid container direction="column">
        {infoSection()}
        {shippingSection()}
        {paymentSection()}
        {!isWarranty && !processing && (
          <Disclaimer
            applicationFlowToken={
              orderData.installment_summary.application_flow_token
            }
            paymentSchedule={orderData.payment_due.payment_schedule}
            northernIrelandOrder={orderData.is_northern_ireland_order}
            {...(disclosureDocumentToken
              ? {
                  disclosureDocumentToken,
                  toggleCheckbox: (checked) => {
                    setDisclosureAcknowledged(checked);
                  },
                }
              : {})}
          />
        )}
        <div style={{ paddingTop: theme.spacing(3) }}>
          <SubmitWithRecaptcha
            {...(processing && { disabled: true })}
            orderData={orderData}
            setRecaptchaResponse={setRecaptchaResponse}
            submit={submit}
            {...(disclosureDocumentToken && !disclosureAcknowledged
              ? {
                  onClick: () => {
                    setToast({
                      open: true,
                      message: t(
                        'checkout.review.disclaimer.financing_disclosures.checkboxErrorMessage',
                      ),
                      variant: 'critical',
                    });
                  },
                }
              : {})}
          />
        </div>
      </Grid>
      {isDashboardMode() && (
        <DashboardModeFooter disclaimers={shopFooterDisclaimers()} />
      )}
    </>
  );
};

export default Review;
